import type { MutationHookOptions } from "@apollo/client";
import { useMutation, gql } from "@apollo/client";

import type { BusinessInfoFieldsType } from "@shared/components/Fields/BusinessInfoFields/BusinessInfoFields";
import type { PersonalInfoFieldsType } from "@shared/components/Fields/PersonalInfoFields/PersonalInfoFields";
import type { AddressInformationData } from "@shared/types/fields/addressInformationTypes";
import type { BankAccountInfoFieldValues } from "@shared/types/fields/bankInformationTypes";
import type {
  BusinessAddressFieldErrors,
  InvoiceAddressAttributes,
  SettingsPaymentsAndTaxesAttributes,
} from "@shared/types/graphqlGenerated";

const MerchantData_Shared_Mutation = gql`
  mutation MerchantData_Shared_Mutation($input: OfficeSettingsPaymentsAndTaxesSetupInput!) {
    office {
      settingsPaymentsAndTaxesSetup(input: $input) {
        merchantData {
          id
          accountType
          classification
          birthDay
          birthMonth
          birthYear
          businessName
          businessNumber
          city
          employerId
          firstName
          lastName
          lastVerifiedTaxInformationAt
          phone
          postalCode
          region
          streetAddress
          streetAddress2
          invoiceAddress {
            id
            address1
            address2
            city
            state
            zipcode
          }
        }
        errors {
          message
          fields {
            accountType
            bankAccountHeldBy
            bankAccountHolder
            bankAccountNumber
            bankAccountType
            bankRoutingNumber
            dateOfBirth
            businessName
            city
            classification
            employerId
            firstName
            lastName
            phone
            postalCode
            region
            profitMargin
            ssn
            streetAddress
          }
        }
      }
    }
  }
`;

interface MerchantDataErrors {
  message?: string;
  fields?: {
    accountType?: string[];
    bankAccountHeldBy?: string[];
    bankAccountHolder?: string[];
    bankAccountNumber?: string[];
    bankAccountType?: string[];
    bankRoutingNumber?: string[];
    dateOfBirth?: string[];
    businessName?: string[];
    city?: string[];
    classification?: string[];
    employerId?: string[];
    firstName?: string[];
    lastName?: string[];
    phone?: string[];
    postalCode?: string[];
    region?: string[];
    profitMargin?: number;
    ssn?: string[];
    streetAddress?: string[];
    jobTitle?: string[];
    email?: string[];
    invoiceAddress?: BusinessAddressFieldErrors;
  };
}

interface MerchantDataMutationData {
  office: {
    settingsPaymentsAndTaxesSetup: {
      merchantData: SettingsPaymentsAndTaxesAttributes;
      errors: MerchantDataErrors;
    };
  };
}

interface Variables {
  input: {
    attributes: {
      accountType: BusinessInfoFieldsType["accountType"];
      businessName?: BusinessInfoFieldsType["businessName"];
      employerId?: BusinessInfoFieldsType["employerId"];
      classification?: BusinessInfoFieldsType["classification"];
      firstName: PersonalInfoFieldsType["firstName"];
      lastName: PersonalInfoFieldsType["lastName"];
      dateOfBirth: PersonalInfoFieldsType["dateOfBirth"];
      ssn: PersonalInfoFieldsType["ssn"];
      streetAddress: AddressInformationData["streetAddress"];
      streetAddress2?: AddressInformationData["streetAddress2"];
      city: AddressInformationData["city"];
      region: AddressInformationData["region"];
      profitMargin?: number;
      postalCode: AddressInformationData["postalCode"];
      phone?: AddressInformationData["phone"];
      bankRoutingNumber?: BankAccountInfoFieldValues["routingNumber"];
      bankAccountNumber?: BankAccountInfoFieldValues["accountNumber"];
      bankAccountType?: BankAccountInfoFieldValues["accountType"];
      bankAccountHeldBy?: BankAccountInfoFieldValues["accountHeldBy"];
      bankAccountHolder?: BankAccountInfoFieldValues["accountHolder"];
      isOauth?: boolean;
      invoiceAddress?: InvoiceAddressAttributes;
      useBusinessAddressAsInvoiceAddress?: boolean;
    };
  };
}

const useMerchantDataMutation = (
  options?: MutationHookOptions<MerchantDataMutationData, Variables>
) => useMutation<MerchantDataMutationData, Variables>(MerchantData_Shared_Mutation, options);

/* eslint-disable @fullscript/gql-no-manual-hook-declaration */
export {
  MerchantData_Shared_Mutation,
  useMerchantDataMutation,
  type MerchantDataMutationData,
  type MerchantDataErrors,
};
